import { useMemo, ReactNode, useCallback, ComponentType } from 'react';
import {
  ButtonLink,
  Tabs,
  TabsClickEventType,
  Text,
  withScreenResizeDetectHoc,
  WithScreenResizePropsType,
} from '@wildberries/ui-kit';
import classnames from 'classnames/bind';
import { Router, State } from 'router5';
import { compose } from 'redux';
import { withRoute, withRouter } from 'react-router5';
import { USER_PERMISSIONS } from '@wildberries/databus-service-user';
import { subscriber } from '@ravilm/databus-react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import { MOBILE_WIDTH } from '@wildberries/ui-kit/lib/utils/screen-type-detect';
import {
  withAnalytics,
  SendAnalyticsType,
} from '@wildberries/wb-front-analytics-tracker';
import { selectedSupplierIdSelector } from '@wildberries/databus-service-suppliers';
import { ANALYTICS } from '@/pages/supplies-management/_constants';
import {
  COMMON_TRANSLATIONS,
  MAIN_PAGE_TRANSLATIONS,
} from '@/pages/supplies-management/_constants/translations';
import { qrSuppliesShownSubscriberSelector } from '@/pages/supplies-management/databus/user/selectors';
import { createSupplyActionSaga } from '@/pages/supplies-management/_redux/create-supply-module/actions';
import {
  TAdditionalInstruction,
  TCreateSupply,
} from '@/pages/supplies-management/_layout/main-layout/types';
import { Instructions } from '@/pages/supplies-management/_components/instructions';
import { TReduxStorage } from '@/pages/supplies-management/_types/redux-storage';
import { commentTextSelector } from '@/pages/supplies-management/_components/connected-ban-modal/redux/supplies-ban-module';
import { getSelectedTabId } from '../_utils/get-selected-tab';
import { getTabs } from './_utils/get-tabs';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Main-layout';

type TReduxProps = {
  sendAnalytics: SendAnalyticsType;
};

type TWithRouterProps = {
  router: Router;
  route: State;
};

type TComponentProps = {
  isQrSuppliesShown?: boolean;
  isShowInstructions?: boolean;
  additionalInstruction?: TAdditionalInstruction;
  children: ReactNode;
  createSupply?: TCreateSupply;
};

type TProps = TComponentProps &
  TReduxProps &
  TWithRouterProps &
  WithScreenResizePropsType &
  TMapStateToProps &
  TMapDispatchToProps;

type TMapStateToProps = {
  banText: string;
};

type TMapDispatchToProps = {
  onCreateSupply: typeof createSupplyActionSaga;
};

const WrappedComponent = ({
  children,
  router,
  route,
  isQrSuppliesShown,
  onCreateSupply,
  createSupply,
  additionalInstruction,
  isShowInstructions,
  windowWidth,
  banText,
  sendAnalytics,
}: TProps) => {
  const tabs = getTabs(isQrSuppliesShown);

  const handleChangeTabs = ({ link }: TabsClickEventType) => {
    router.navigate(link);
  };

  const selectedTabId = useMemo(
    () =>
      getSelectedTabId({
        routeName: route.name,
        tabsConfig: tabs,
      }),
    [route.name, tabs],
  );

  const isMobile = useMemo(() => {
    return windowWidth <= MOBILE_WIDTH;
  }, [windowWidth]);

  const onCreateSupplyFn = useCallback(() => {
    const idSuppler = selectedSupplierIdSelector();
    sendAnalytics({
      name: ANALYTICS.SupplierShipmentCreateT,
      params: {
        idSuppler,
      },
    });
    onCreateSupply();
  }, [onCreateSupply, sendAnalytics]);

  const isShowCreateSupply = useMemo(() => {
    if (isQrSuppliesShown && createSupply === 'allSupplies') {
      return false;
    }

    return !!createSupply;
  }, [createSupply, isQrSuppliesShown]);

  const isShowCreateSupplyButtonDesktop = useMemo(
    () => !isMobile && isShowCreateSupply,
    [isMobile, isShowCreateSupply],
  );
  const isShowCreateSupplyButtonMobile = useMemo(() => isMobile, [isMobile]);

  const createSupplyButton = useMemo(
    () => (
      <ButtonLink
        disabled={Boolean(banText)}
        onClick={onCreateSupplyFn}
        text={i18next.t(COMMON_TRANSLATIONS.creatSupply)}
        variant="accent"
      />
    ),
    [onCreateSupplyFn, banText],
  );

  return (
    <div className={cn(BLOCK_NAME)}>
      <div className={cn(`${BLOCK_NAME}__content`)}>
        <div className={cn(`${BLOCK_NAME}__title-wrapper`)}>
          <Text
            color="black"
            size="h1"
            text={i18next.t(MAIN_PAGE_TRANSLATIONS.title)}
          />
          {(isShowCreateSupply || isShowInstructions) && (
            <div className={cn(`${BLOCK_NAME}__title-content-right`)}>
              {isShowInstructions && (
                <Instructions
                  additionalInstruction={additionalInstruction}
                  isMobile={isMobile}
                />
              )}
              {isShowCreateSupplyButtonDesktop && createSupplyButton}
            </div>
          )}
        </div>
        <div className={cn(`${BLOCK_NAME}__header`)}>
          <div className={cn(`${BLOCK_NAME}__tabs`)}>
            <Tabs
              moreButtonText={i18next.t(COMMON_TRANSLATIONS.moreButton)}
              onChange={handleChangeTabs}
              selectedId={selectedTabId}
              tabs={tabs}
              variant="section"
            />
          </div>
        </div>
        {isShowCreateSupplyButtonMobile && createSupplyButton}
        {children}
      </div>
    </div>
  );
};

const mapDispatchToProps: TMapDispatchToProps = {
  onCreateSupply: createSupplyActionSaga,
};

const mapStateToProps = (state: TReduxStorage): TMapStateToProps => ({
  banText: commentTextSelector(state),
});

export const MainLayout = compose<ComponentType<TComponentProps>>(
  connect(mapStateToProps, mapDispatchToProps),
  withAnalytics,
  withRouter,
  withRoute,
  withScreenResizeDetectHoc<Omit<TProps, 'windowWidth' | 'screenType'>>({
    withWidthCapture: true,
  }),
  subscriber({
    getStateToProps: {
      [USER_PERMISSIONS]: {
        isQrSuppliesShown: qrSuppliesShownSubscriberSelector,
      },
    },
  }),
)(WrappedComponent);
